import * as React from 'react';
import { ChevronDown, User as UserIcon } from 'lucide-react';
import type {
  ClientLight,
  ClientListLightQuery,
  ClientListLightResponse,
} from '@proptr/shared';
import { AppShell } from '../components/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

type SortKey = NonNullable<ClientListLightQuery['sort']>;

const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
];

export function ContactsPage() {
  const [sortBy, setSortBy] = React.useState<SortKey>('latestInquiry');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);

  const [data, setData] = React.useState<ClientListLightResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    client.clients
      .listClientsLight({ sort: sortBy, order, page, limit: pageSize })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, [sortBy, order, page, pageSize]);

  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const totalPages = meta?.pages ?? 1;
  const items = data?.items ?? null;

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            Contacts
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            <span className="text-foreground">{total}</span> total
          </p>
        </div>

        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[1200px]">
              {/* Table header — column widths/flex must stay in sync with
                  ContactRow's LabeledCell widths below. */}
              <div className="flex items-center gap-4 border-b border-[hsl(var(--hairline))] px-5 py-3">
                <span className="h-8 w-8 shrink-0" aria-hidden />
                <span className="h-10 w-10 shrink-0" aria-hidden />
                <HeaderLabel className="min-w-[180px] flex-[2]">
                  Name
                </HeaderLabel>
                <HeaderLabel className="min-w-[160px] flex-[1.5]">
                  Phone
                </HeaderLabel>
                <HeaderLabel className="min-w-[200px] flex-[2.5]">
                  Email
                </HeaderLabel>
                <HeaderLabel className="min-w-[180px] flex-[2]">
                  Latest inquiry
                </HeaderLabel>
                <HeaderLabel className="min-w-[120px] flex-1 text-right">
                  Total inquiries
                </HeaderLabel>
              </div>
              {error ? (
                <div className="px-5 py-12 text-center font-mono text-[11px] text-destructive">
                  {error}
                </div>
              ) : items === null ? (
                <div className="px-5 py-12 text-center font-mono text-[11px] text-muted-foreground">
                  Loading…
                </div>
              ) : items.length === 0 ? (
                <div className="px-5 py-12 text-center font-mono text-[11px] text-muted-foreground">
                  No contacts yet.
                </div>
              ) : (
                items.map((contact) => (
                  <ContactRow key={contact._id} contact={contact} />
                ))
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[hsl(var(--hairline))] px-5 py-3.5">
            <div className="mono-label text-muted-foreground">
              Showing{' '}
              <span className="text-foreground">
                {start}–{end}
              </span>{' '}
              of <span className="text-foreground">{total}</span> results
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="mono-label text-muted-foreground">
                  Page limit
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline">
                      {pageSize}
                      <ChevronDown />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[5rem]">
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        onSelect={() => {
                          setPageSize(Number(opt.value));
                          setPage(1);
                        }}
                        className={cn(
                          String(pageSize) === opt.value && 'text-foreground',
                        )}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

function ContactRow({ contact }: { contact: ClientLight }) {
  const [open, setOpen] = React.useState(false);
  const name = contact.name || '—';
  const phone = contact.phone || '—';
  const email = contact.email || '—';
  const latest = formatDateTime(contact.latestInquiry);

  return (
    <div
      className={cn(
        'border-b border-[hsl(var(--hairline))] last:border-b-0',
        open && 'bg-muted/20',
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground">
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 transition-transform',
              open && 'rotate-180',
            )}
          />
        </span>
        <ContactAvatar name={name} image={contact.image} />
        <LabeledCell
          className="min-w-[180px] flex-[2]"
          value={name}
          label="Name"
        />
        <LabeledCell
          className="min-w-[160px] flex-[1.5]"
          value={phone}
          label="Phone"
          mono
        />
        <LabeledCell
          className="min-w-[200px] flex-[2.5]"
          value={email}
          label="Email"
        />
        <LabeledCell
          className="min-w-[180px] flex-[2]"
          value={latest}
          label="Latest inquiry"
          mono
        />
        <LabeledCell
          className="min-w-[120px] flex-1 text-right"
          value={String(contact.totalInquiries)}
          label="Total inquiries"
          mono
          alignRight
        />
      </button>
    </div>
  );
}

// Column-aligned label for the table header. Same role as the header rows
// in InquiriesPage / AgentsPage / ListingsPage.
function HeaderLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mono-label text-muted-foreground', className)}>
      {children}
    </div>
  );
}

// Cell for a contact row. The `label` prop is accepted for backwards-compat
// with the old inline-label layout but no longer rendered — the table now
// has a proper top header row, so per-row labels would be redundant.
function LabeledCell({
  value,
  className,
  mono,
  alignRight,
}: {
  value: string;
  /** Unused — kept on the type so call sites compile unchanged. */
  label?: string;
  className?: string;
  mono?: boolean;
  alignRight?: boolean;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div
        className={cn(
          'truncate font-sans text-[14px] font-medium text-foreground',
          mono && 'font-mono tabular-nums',
          alignRight && 'text-right',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function ContactAvatar({
  name,
  image,
}: {
  name: string;
  image?: string | null;
}) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-10 w-10 shrink-0 rounded-full bg-muted object-cover"
      />
    );
  }
  if (name && name !== '—') {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
        {initials(name)}
      </div>
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
      <UserIcon className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toISOString().slice(0, 10);
  const time = d
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
  return `${date} ${time}`;
}
